import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { PartnerAutocomplete } from '@/components/ui/PartnerAutocomplete';
import { PartnerDisplay } from '@/components/ui/PartnerDisplay';
import { TrainingPartner } from '@/types/trainings';
import { RollCategoriesSection, IRollCategories } from '@/features/calendar/components/RollCategoriesSection';

export interface ITrainingPartnersSectionProps {
  localPartners: TrainingPartner[];
  partnersLoading: boolean;
  partnersSaving: boolean;
  partnerSearchValue: any;
  setPartnerSearchValue: (value: any) => void;
  reloadingPartnerNotes: boolean;
  hasPartnerNotes: (partner: TrainingPartner) => boolean;
  getPartnerNotes: (partner: TrainingPartner) => any;
  handlePartnerSelect: (partner: TrainingPartner | string | null) => void;
  handleRemovePartner: (partnerId: string) => void;
  onViewFeedback: (partner: TrainingPartner) => Promise<void>;
  onAddFeedback: (partner: TrainingPartner) => Promise<void>;
  saving: boolean;
  // Roll categories props
  getRollCategories: (partner: TrainingPartner) => IRollCategories;
  handleRollCategoryChange: (partnerKey: string, category: string, value: string) => void;
  rollCategoriesOpenCategories: Map<string, string[]>;
  setRollCategoriesOpenCategoriesForPartner: (partnerKey: string, categories: string[] | ((prev: string[]) => string[])) => void;
  getShowRollCategories: (partner: TrainingPartner) => boolean;
  setShowRollCategoriesForPartner: (partnerKey: string, show: boolean | ((prev: boolean) => boolean)) => void;
}

export const TrainingPartnersSection: React.FC<ITrainingPartnersSectionProps> = ({
  localPartners,
  partnersLoading,
  partnersSaving,
  partnerSearchValue,
  setPartnerSearchValue,
  reloadingPartnerNotes,
  hasPartnerNotes,
  getPartnerNotes,
  handlePartnerSelect,
  handleRemovePartner,
  onViewFeedback,
  onAddFeedback,
  saving,
  getRollCategories,
  handleRollCategoryChange,
  rollCategoriesOpenCategories,
  setRollCategoriesOpenCategoriesForPartner,
  getShowRollCategories,
  setShowRollCategoriesForPartner,
}) => {
  const { t, currentLanguage } = useI18n();

  return (
    <div className="space-y-3 !mt-8">
      <label className="block text-sm font-medium text-black">
        {t('calendar.training_partners')}
      </label>
      
      {/* Partner Search */}
      {!partnersLoading && (
        <PartnerAutocomplete
          key={`partner-search-${localPartners.length}`}
          value={partnerSearchValue}
          onChange={handlePartnerSelect}
          placeholder={t('calendar.type_name_or_username')}
          containerClassName="bg-white"
          disabled={saving}
        />
      )}

      {/* Loading Shimmer */}
      {partnersLoading && (
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
          <div className="space-y-2">
            {/* Shimmer Partner 1 */}
            <div className="rounded-lg overflow-hidden relative">
              <div className="p-3 pr-12">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-32 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
                  </div>
                </div>
              </div>
              <div className="px-3 pb-3">
                <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
            
            {/* Shimmer Partner 2 */}
            <div className="rounded-lg overflow-hidden relative">
              <div className="p-3 pr-12">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-28 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded animate-pulse w-16"></div>
                  </div>
                </div>
              </div>
              <div className="px-3 pb-3">
                <div className="flex gap-2">
                  <div className="h-6 bg-gray-200 rounded animate-pulse flex-1"></div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse flex-1"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Partners List */}
      {!partnersLoading && localPartners.length > 0 && (
        <div className="space-y-2" data-testid="training-partners-list">
          <div className="text-xs text-gray-600">
            {(() => {
              const pluralSuffix = localPartners.length !== 1 ? 's' : '';
              return t('calendar.selected_partners', { 
                count: localPartners.length,
                plural: pluralSuffix
              }).replace(/\{plural\}/g, pluralSuffix);
            })()}
          </div>
          <div className="space-y-2">
            {localPartners.map((partner, index) => {
              const hasExistingFeedback = hasPartnerNotes(partner);
              const existingFeedbackData = getPartnerNotes(partner);
              const partnerKey = partner.partner_user_id || partner.id;
              const rollCategories = getRollCategories(partner);
              const openCategories = rollCategoriesOpenCategories.get(partnerKey) || [];
              const showRollCategories = getShowRollCategories(partner);
              
              // Count selected values - check if value exists and is not empty
              const selectedCount = Object.values(rollCategories).filter(v => v && typeof v === 'string' && v.trim() !== '').length;
              
              return (
                <div key={partner.id} className="rounded-lg overflow-hidden relative">
                  {/* Remove Button - Top Right Corner */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemovePartner(partner.id)}
                    className="absolute top-2 right-2 h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  
                  {/* Roll Number */}
                  <div className="px-3 pt-3 pb-1">
                    <span className="text-xs font-medium text-gray-700">
                      {t('calendar.roll_number', { number: index + 1 })}
                    </span>
                  </div>
                  
                  {/* Partner Info Row */}
                  <div className="p-3 pr-12">
                    <PartnerDisplay
                      partner={partner}
                      showRemoveButton={false}
                    />
                  </div>
                  
                  {/* Roll Categories Section */}
                  <div className="px-3 pb-3">
                    {reloadingPartnerNotes ? (
                      <div className="space-y-3">
                        {/* Header Shimmer */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
                        </div>
                        {/* Badges Shimmer */}
                        <div className="flex flex-wrap gap-1 mb-3">
                          {[1, 2].map((i) => (
                            <div key={i} className="h-6 bg-gray-200 rounded-full animate-pulse w-20"></div>
                          ))}
                        </div>
                        {/* Categories Shimmer */}
                        <div className="space-y-2">
                          {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <label className="text-sm font-medium text-black">
                              {t('calendar.roll_categories')}
                            </label>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowRollCategoriesForPartner(partnerKey, (prev) => !prev)}
                            className="text-xs text-sossiety-red hover:text-sossiety-red-hover font-medium underline"
                          >
                            {showRollCategories ? t('calendar.hide_roll_categories') : t('calendar.show_roll_categories')}
                          </button>
                        </div>
                        
                        {!showRollCategories && selectedCount === 0 && (
                          <div className="text-center mt-3 mb-3">
                            <p className="text-xs font-medium text-gray-600">
                              {t('calendar.register_roll_categories')}
                            </p>
                          </div>
                        )}
                        
                        <RollCategoriesSection
                          rollCategories={rollCategories}
                          onCategoryChange={(category, value) => handleRollCategoryChange(partnerKey, category, value)}
                          openCategories={openCategories}
                          setOpenCategories={(categories) => setRollCategoriesOpenCategoriesForPartner(partnerKey, categories)}
                          showRollCategories={showRollCategories}
                        />
                      </>
                    )}
                  </div>
                  
                  {/* Feedback Buttons */}
                  <div className="px-3 pb-3">
                    {reloadingPartnerNotes ? (
                      <div className="w-full h-8 bg-gray-200 rounded-full animate-pulse"></div>
                    ) : hasExistingFeedback ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          await onViewFeedback(partner);
                        }}
                        className="w-full border-black text-black hover:bg-gray-50 rounded-full text-xs"
                        data-testid={`view-feedback-button-${partner.partner_user_id || partner.id}`}
                        data-view-note-button="true"
                      >
                        {t('calendar.view_feedback')}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          await onAddFeedback(partner);
                        }}
                        className="w-full border-black text-black hover:bg-gray-50 rounded-full text-xs"
                        data-testid={`add-feedback-button-${partner.partner_user_id || partner.id}`}
                      >
                        {t('calendar.add_feedback')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

